import { useState, useEffect, useRef } from "react";
import "./FeedbackPopUp.css";
import { getFeedbackIssues, submitFeedback, dismissFeedback } from "../../../services/FeedbackApi"; // adjust path
import useFeedbackPopupStore from "../../../store/useFeedbackPopupStore";
import { SuccessAlert } from "../../common/ErrorAlert/SuccessAlert";

const emojis = [
  { value: "very", rating: 1, emoji: "😡", label: "VERY" },
  { value: "poor", rating: 2, emoji: "😐", label: "POOR" },
  { value: "okay", rating: 3, emoji: "🙂", label: "OKAY" },
  { value: "good", rating: 4, emoji: "😊", label: "GOOD" },
  { value: "excellent", rating: 5, emoji: "😍", label: "EXCELLENT" },
];

export default function FeedbackPopup() {
  const [selected, setSelected] = useState(null);   // { value, rating, emoji, label }
  const [chips, setChips] = useState([]);      // [{ id, name }, ...]
  const [selectedChips, setSelectedChips] = useState([]);      // [{ id, name }, ...]
  const [feedback, setFeedback] = useState("");
  const [showError, setShowError] = useState(false);
  const { showPopup, clearFeedbackPopup } = useFeedbackPopupStore();
  const [submitting, setSubmitting] = useState(false);
  const [chipsLoading, setChipsLoading] = useState(true);
  const scrollRef = useRef(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      setHasScrollbar(el.scrollHeight > el.clientHeight);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [showPopup]);

  // Fetch issue chips when popup is opened
  useEffect(() => {
    if (showPopup) {
      setChipsLoading(true);
      getFeedbackIssues()
        .then((res) => setChips(res.data))
        .catch((err) => console.error("Failed to load feedback issues:", err))
        .finally(() => setChipsLoading(false));
    }
  }, [showPopup]);

  // Reset state when popup is opened
  useEffect(() => {
    if (showPopup) {
      setSelected(null);
      setSelectedChips([]);
      setFeedback("");
      setShowError(false);
    }
  }, [showPopup]);

  if (!showPopup) return null;

  // const isLowRating = selected?.rating <= 2;

  const toggleChip = (chip) => {
    setSelectedChips((prev) => {
      const exists = prev.find((c) => c.id === chip.id);
      if (exists) return prev.filter((c) => c.id !== chip.id);
      if (prev.length >= 3) return prev;
      return [...prev, chip];
    });
  };

  const handleEmojiSelect = (emoji) => {
    setSelected(emoji);
    setSelectedChips([]);
    setFeedback("");
    setShowError(false);
  };

  const handleSubmit = async () => {
    if (!selected || selectedChips.length === 0 || feedback.trim() === "") {
      setShowError(true);
      return;
    }

    setShowError(false);
    setSubmitting(true);

    try {
      await submitFeedback({
        rating: selected.rating,
        issues: selectedChips.map((c) => c.id),
        comment: feedback.trim(),
      });
      clearFeedbackPopup();
      SuccessAlert("Thank you! Your feedback has been submitted.");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    await dismissFeedback("maybe_later");
    clearFeedbackPopup();
    SuccessAlert("We'll remind you again in 24 hours.");
  };

  const handleDontShowMonth = async () => {
    await dismissFeedback("dont_show_month");
    clearFeedbackPopup();
    SuccessAlert("Got it! We won't show this again for a month.");
  };

  const isSubmitDisabled =
    !selected ||
    selectedChips.length === 0 ||
    feedback.trim() === "";

  return (
    <div className="feedback-overlay">
      <div className="feedback-popup">
        <div ref={scrollRef} className={`feedback-scroll-container ${hasScrollbar ? "has-scrollbar" : ""}`}>
          {/* Close */}
          <button className="feedback-close"
            onClick={handleClose}
          >
            ✕
          </button>
          {/* Header */}
          <div className="feedback-header">
            <div className="feedback-header-title-content">
              <span className="feedback-title">Help us improve Salesroute</span>
            </div>
            <div className="feedback-header-subtitle-content">
              <span className="feedback-subtitle">
                Your feedback takes less than 30 seconds
              </span>
            </div>
          </div>

          <div className="feedback-question-content">
            <span className="feedback-question">
              How was your experience today?
            </span>
          </div>

          {/* Emojis */}
          <div className="feedback-emoji-row">
            {emojis.map((item) => {
              const isSelected = selected?.value === item.value;
              return (
                <div
                  key={item.value}
                  className="feedback-emoji-option"
                  onClick={() => handleEmojiSelect(item)}
                >
                  <button
                    className={`feedback-emoji-btn ${isSelected ? "selected" : ""}`}
                  >
                    <span className="emoji">{item.emoji}</span>
                  </button>
                  <span className={`feedback-emoji-label ${isSelected ? "selected" : ""}`}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step 2 */}
          {selected && (
            <div className="feedback-step2">

              {/* Chips */}
              <div className="feedback-chips-section">
                <span className="feedback-chips-title">
                  What could be better?{" "}
                  <span className="feedback-chips-limit">(up to 3){" "}</span>
                  <span className="feedback-required">*</span>
                </span>
                <div className="feedback-chips-row">
                  {chipsLoading ? (
                    <span className="feedback-chips-loading">Loading...</span>
                  ) : (
                    chips.map((chip) => (
                      <button
                        key={chip.id}
                        className={`feedback-chip ${selectedChips.find((c) => c.id === chip.id) ? "selected" : ""
                          }`}
                        onClick={() => toggleChip(chip)}
                      >
                        {chip.name}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Textarea */}
              <div className="feedback-textarea-section">
                <p className="feedback-textarea-label">
                  Tell us more about your feedback{" "}
                  <span className="feedback-required">*</span>
                </p>
                <p className="feedback-textarea-hint">
                  This helps us understand the issue better
                </p>
                <textarea
                  className={`feedback-textarea ${showError ? "error" : ""}`}
                  placeholder="Example: Reports page takes too long to load on mobile."
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                    if (e.target.value.trim()) setShowError(false);
                  }}
                />
                {showError && (
                  <div className="feedback-error">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" className="feedback-error-svg">
                      <g clipPath="url(#clip0_20151_12956)">
                        <path d="M7.00033 12.8334C10.222 12.8334 12.8337 10.2217 12.8337 7.00008C12.8337 3.77842 10.222 1.16675 7.00033 1.16675C3.77866 1.16675 1.16699 3.77842 1.16699 7.00008C1.16699 10.2217 3.77866 12.8334 7.00033 12.8334Z" stroke="#FF2056" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 4.66675V7.00008" stroke="#FF2056" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 9.33325H7.00583" stroke="#FF2056" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_20151_12956">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span>Please provide a brief explanation for the low rating.</span>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                className="feedback-submit"
                onClick={handleSubmit}
                disabled={submitting || isSubmitDisabled}
              >
                {submitting ? "Submitting..." : "Submit feedback"}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className={`feedback-footer ${selected ? "with-step2" : ""}`}>
            <span
              className="feedback-maybe"
              onClick={handleClose}
            >
              Maybe later
            </span>

            <span
              className="feedback-dont"
              onClick={handleDontShowMonth}
            >
              Don't show again this month
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}